(() => {
  'use strict';

  const API_REVISION = '2026-07-15';
  const REQUEST_TIMEOUT_MS = 12000;
  const SUBSCRIPTION_CACHE_MS = 30 * 60 * 1000;

  /**
   * Initialiserer alle Coming Soon-komponenter på siden.
   * Understøtter også genindlæsning i Shopify Theme Editor.
   */
  function initialise(root = document) {
    const components = root.querySelectorAll(
      '[data-coming-soon-component]:not([data-coming-soon-initialized])'
    );

    components.forEach((component) => {
      component.dataset.comingSoonInitialized = 'true';

      const form = component.querySelector('[data-coming-soon-form]');
      const emailInput = form?.querySelector('input[name="email"]');
      const honeypot = form?.querySelector('input[name="company"]');
      const submitButton = component.querySelector(
        '[data-coming-soon-submit]'
      );
      const buttonText = component.querySelector(
        '[data-coming-soon-button-text]'
      );
      const message = component.querySelector(
        '[data-coming-soon-message]'
      );
      const success = component.querySelector(
        '[data-coming-soon-success]'
      );

      if (
        !form ||
        !emailInput ||
        !submitButton ||
        !buttonText ||
        !message ||
        !success
      ) {
        console.warn('Coming Soon: Formularen mangler nødvendige elementer.');
        return;
      }

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        clearMessage(message);
        emailInput.removeAttribute('aria-invalid');

        const email = emailInput.value.trim().toLowerCase();

        if (!email || !emailInput.validity.valid) {
          emailInput.setAttribute('aria-invalid', 'true');
          showError(
            message,
            'Indtast venligst en gyldig e-mailadresse.'
          );
          emailInput.focus();
          return;
        }

        /*
         * Simpel botbeskyttelse.
         * Mennesker ser ikke og udfylder ikke honeypot-feltet.
         */
        if (honeypot?.value) {
          form.reset();
          showSuccess(form, success);
          return;
        }

        const config = getComponentConfig(component);

        if (!config.siteId || !config.listId || !config.productHandle) {
          console.error('Coming Soon: Mangelfuld konfiguration.', config);
          showError(
            message,
            'Formularen er ikke konfigureret korrekt. Prøv igen senere.'
          );
          return;
        }

        setLoading(submitButton, buttonText, true);

        try {
          /*
           * Klaviyo anbefaler, at samme profil ikke sendes gennem
           * gentagne subscription-kald inden for kort tid.
           *
           * Vi husker derfor et vellykket subscription-kald lokalt
           * i browseren i 30 minutter. Hvis property-opdateringen
           * fejler, kan brugeren prøve igen uden straks at abonnere igen.
           */
          if (!hasRecentSubscription(email, config.listId)) {
            await subscribeProfile(email, config);
            rememberSubscription(email, config.listId);
          }

          /*
           * Custom properties opdateres i et separat API-kald,
           * efter at subscription-kaldet er accepteret.
           */
          await updateProfileProperties(email, config);

          form.reset();
          showSuccess(form, success);
          trackSignup(config);
        } catch (error) {
          console.error('Coming Soon signup failed:', error);

          const errorText =
            error.name === 'AbortError'
              ? 'Det tog for lang tid at sende. Prøv venligst igen.'
              : 'Vi kunne ikke gemme din tilmelding. Prøv venligst igen.';

          showError(message, errorText);
        } finally {
          setLoading(submitButton, buttonText, false);
        }
      });
    });
  }

  /**
   * Henter produkt- og Klaviyo-data fra Liquid-komponentens data-attributter.
   */
  function getComponentConfig(component) {
    return {
      siteId: component.dataset.klaviyoSiteId || '',
      listId: component.dataset.klaviyoListId || '',
      productId: component.dataset.productId || '',
      productTitle: component.dataset.productTitle || '',
      productHandle: component.dataset.productHandle || '',
      productUrl: component.dataset.productUrl || '',
      locale: component.dataset.locale || 'da-DK'
    };
  }

  /**
   * Kald 1:
   * Tilmeld profilen til email marketing og listen Produktlanceringer.
   */
  async function subscribeProfile(email, config) {
    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'KJÆRLI product launch notification',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                subscriptions: {
                  email: {
                    marketing: {
                      consent: 'SUBSCRIBED'
                    }
                  }
                }
              }
            }
          }
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: config.listId
            }
          }
        }
      }
    };

    await klaviyoRequest({
      endpoint: 'subscriptions',
      siteId: config.siteId,
      payload
    });
  }

  /**
   * Kald 2:
   * Opretter eller opdaterer profilens custom properties.
   *
   * "Last"-felterne gør det nemt at se seneste tilmelding.
   * Array-properties gør det muligt at gemme interesse for flere produkter.
   */
  async function updateProfileProperties(email, config) {
    const payload = {
      data: {
        type: 'profile',
        attributes: {
          email,
          locale: config.locale,
          properties: {
            'Product Launch Last Interest': config.productTitle,
            'Product Launch Last Handle': config.productHandle,
            'Product Launch Last Product ID': config.productId,
            'Product Launch Last URL': config.productUrl,
            'Product Launch Last Requested At': new Date().toISOString()
          }
        },
        meta: {
          patch_properties: {
            append: {
              'Product Launch Interests': [config.productHandle],
              'Product Launch Interest Titles': [config.productTitle]
            }
          }
        }
      }
    };

    await klaviyoRequest({
      endpoint: 'profiles',
      siteId: config.siteId,
      payload
    });
  }

  /**
   * Fælles funktion til Klaviyos offentlige Client API.
   */
  async function klaviyoRequest({ endpoint, siteId, payload }) {
    const controller = new AbortController();

    const timeout = window.setTimeout(
      () => controller.abort(),
      REQUEST_TIMEOUT_MS
    );

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/${endpoint}?company_id=${encodeURIComponent(
          siteId
        )}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/vnd.api+json',
            'Content-Type': 'application/vnd.api+json',
            revision: API_REVISION
          },
          body: JSON.stringify(payload),
          signal: controller.signal
        }
      );

      if (!response.ok) {
        const detail = await getKlaviyoErrorDetail(response);

        throw new Error(
          detail ||
            `Klaviyo ${endpoint} request failed: ${response.status}`
        );
      }
    } finally {
      window.clearTimeout(timeout);
    }
  }

  /**
   * Forsøger at hente den konkrete fejlbesked fra Klaviyo.
   */
  async function getKlaviyoErrorDetail(response) {
    try {
      const body = await response.json();

      return (
        body?.errors?.[0]?.detail ||
        body?.errors?.[0]?.title ||
        ''
      );
    } catch (_error) {
      return '';
    }
  }

  /**
   * Undgår gentagne subscription-kald inden for 30 minutter
   * fra den samme browser.
   */
  function hasRecentSubscription(email, listId) {
    try {
      const key = getSubscriptionStorageKey(email, listId);
      const storedValue = window.localStorage.getItem(key);

      if (!storedValue) {
        return false;
      }

      const timestamp = Number(storedValue);

      if (!Number.isFinite(timestamp)) {
        window.localStorage.removeItem(key);
        return false;
      }

      return Date.now() - timestamp < SUBSCRIPTION_CACHE_MS;
    } catch (_error) {
      /*
       * localStorage kan være deaktiveret.
       * Formularen skal stadig fungere.
       */
      return false;
    }
  }

  function rememberSubscription(email, listId) {
    try {
      const key = getSubscriptionStorageKey(email, listId);
      window.localStorage.setItem(key, String(Date.now()));
    } catch (_error) {
      // Formularen må gerne fortsætte, selvom localStorage er blokeret.
    }
  }

  function getSubscriptionStorageKey(email, listId) {
    return `coming-soon-subscription:${listId}:${email}`;
  }

  /**
   * Loading-, fejl- og success-visning.
   */
  function setLoading(button, textElement, loading) {
    button.disabled = loading;
    button.setAttribute('aria-busy', String(loading));
    textElement.textContent = loading
      ? 'Gemmer...'
      : 'Giv mig besked';
  }

  function clearMessage(message) {
    message.hidden = true;
    message.textContent = '';
    message.classList.remove('coming-soon__message--error');
  }

  function showError(message, text) {
    message.textContent = text;
    message.classList.add('coming-soon__message--error');
    message.hidden = false;
  }

  function showSuccess(form, success) {
  form.hidden = true;
  form.style.display = 'none';

  success.hidden = false;
  success.style.display = 'block';
}
  

  /**
   * Sender et dataLayer-event, hvis Google Tag Manager eller
   * anden dataLayer-baseret tracking bruges på siden.
   */
  function trackSignup(config) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'product_launch_signup',
      product_id: config.productId,
      product_handle: config.productHandle,
      product_title: config.productTitle
    });
  }

  /**
   * Normal sideindlæsning.
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialise();
    });
  } else {
    initialise();
  }

  /**
   * Shopify Theme Editor kan genindlæse en sektion,
   * uden at hele siden genindlæses.
   */
  document.addEventListener('shopify:section:load', (event) => {
    initialise(event.target);
  });
})();